// ═══════════════════════════════════════════════════════════════
// useIncidentZones — Shared hook for any module to access
// GeoOps zones for the active incident
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { useGeoOpsStore } from '../store/geoopsStore';
import type { GeoOpsZone } from '../store/geoopsStore';
import { isInsideZone } from '../lib/geoopsGeo';

export function useIncidentZones() {
  const { activeIncident } = useAuthStore();
  const { zones, isLoading, loadZones } = useGeoOpsStore();

  useEffect(() => {
    if (activeIncident) loadZones(String(activeIncident.id));
  }, [activeIncident?.id]);

  const incidentZones = useMemo(
    () => zones.filter(z => String(z.incidentId) === String(activeIncident?.id)),
    [zones, activeIncident]
  );

  const activeZones = useMemo(() => incidentZones.filter(z => z.status === 'active'), [incidentZones]);
  const evacuationZones = useMemo(() => incidentZones.filter(z => z.zoneType === 'evacuation_zone'), [incidentZones]);
  const stagingAreas = useMemo(() => incidentZones.filter(z => z.zoneType === 'staging_area'), [incidentZones]);
  const impactZones = useMemo(() => incidentZones.filter(z => z.zoneType === 'impact_zone'), [incidentZones]);

  const getZonesContainingPoint = (lat: number, lng: number): GeoOpsZone[] => {
    return incidentZones.filter(z => z.geometry && isInsideZone(lat, lng, z.geometry));
  };

  return {
    zones: incidentZones,
    activeZones,
    evacuationZones,
    stagingAreas,
    impactZones,
    isLoading,
    refresh: () => activeIncident ? loadZones(String(activeIncident.id)) : Promise.resolve(),
    getZonesContainingPoint,
  };
}
